import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { checkPasswordRequirements } from '../../utils/passwordValidation';

export const DEFAULT_MIN_LENGTH = 8;

interface PasswordRequirementsProps {
  password: string;
  minLength?: number;
  isDarkMode?: boolean;
  sx?: object;
}

/**
 * Shared password requirements hint box: info icon, title, and 5 checks (length, number, uppercase, special, lowercase) with ✓/○.
 * Use under password fields on register, profile, reset/update password, activate, and ChangePasswordDialog.
 */
const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  minLength = DEFAULT_MIN_LENGTH,
  isDarkMode = false,
  sx = {},
}) => {
  const { t } = useTranslation();
  const requirements = checkPasswordRequirements(password, minLength);

  const checkColor = isDarkMode ? '#4caf50' : '#4caf50';
  const emptyColor = isDarkMode ? '#606060' : '#9e9e9e';
  const textColor = isDarkMode ? '#b0b0b0' : '#666666';
  const titleColor = isDarkMode ? '#ffffff' : '#000000';

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
        borderRadius: 1,
        mb: 2,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <InfoOutlinedIcon
          sx={{ fontSize: '20px', color: isDarkMode ? '#b0b0b0' : '#9e9e9e', mr: 1 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: titleColor,
            fontSize: '11px',
          }}
        >
          {t('sentences:pages.profile.password_must_include') ||
            'Your password must include:'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                color: requirements.length ? checkColor : emptyColor,
                fontSize: '16px',
                marginRight: '8px',
              }}
            >
              {requirements.length ? '✓' : '○'}
            </span>
            <Typography variant="body2" sx={{ fontSize: '11px', color: textColor }}>
              {t('sentences:pages.profile.requirement_length') || 'At least 8 characters'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                color: requirements.number ? checkColor : emptyColor,
                fontSize: '16px',
                marginRight: '8px',
              }}
            >
              {requirements.number ? '✓' : '○'}
            </span>
            <Typography variant="body2" sx={{ fontSize: '11px', color: textColor }}>
              {t('sentences:pages.profile.requirement_number') || 'At least 1 number'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                color: requirements.uppercase ? checkColor : emptyColor,
                fontSize: '16px',
                marginRight: '8px',
              }}
            >
              {requirements.uppercase ? '✓' : '○'}
            </span>
            <Typography variant="body2" sx={{ fontSize: '11px', color: textColor }}>
              {t('sentences:pages.profile.requirement_uppercase') || 'At least 1 uppercase letter'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                color: requirements.special ? checkColor : emptyColor,
                fontSize: '16px',
                marginRight: '8px',
              }}
            >
              {requirements.special ? '✓' : '○'}
            </span>
            <Typography variant="body2" sx={{ fontSize: '11px', color: textColor }}>
              {t('sentences:pages.profile.requirement_special') || 'At least 1 special character'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                color: requirements.lowercase ? checkColor : emptyColor,
                fontSize: '16px',
                marginRight: '8px',
              }}
            >
              {requirements.lowercase ? '✓' : '○'}
            </span>
            <Typography variant="body2" sx={{ fontSize: '11px', color: textColor }}>
              {t('sentences:pages.profile.requirement_lowercase') || 'At least 1 lowercase letter'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PasswordRequirements;
